import { z } from "zod";
import { courierProviderSchema } from "./couriers.js";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
/** Fraud check and blocklist (FR-01 … FR-04, B-40; docs/specs/shipping.md). */
export const RISK_LEVELS = ["low", "medium", "high"];
export const riskLevelSchema = z.enum(RISK_LEVELS);
/** Why a level was given; the client words each code (`params` fill it in). */
export const RISK_REASONS = [
    "blocked",
    "new_customer",
    "low_success",
    "medium_success",
    "good_history",
    "many_cancelled",
    "courier_low_success",
];
export const riskReasonSchema = z.object({
    code: z.enum(RISK_REASONS),
    /** e.g. `{ rate: 4200 }` (basis points) or `{ count: 3 }`. */
    params: z.record(z.string(), z.int()),
});
/** Our own orders for the phone (FR-01): delivered counts partial deliveries; the rate is over delivered + returned. */
export const ownHistorySchema = z.object({
    total: z.int(),
    delivered: z.int(),
    returned: z.int(),
    cancelled: z.int(),
    /** Basis points; null until something was delivered or returned. */
    successRateBp: z.int().nullable(),
});
/** FR-02: a courier's delivery history for the phone, cached 24 h. Absent when no courier offers it. */
export const courierHistorySchema = z.object({
    courierAccountId: z.uuid(),
    provider: courierProviderSchema,
    total: z.int(),
    delivered: z.int(),
    returned: z.int(),
    fetchedAt: z.iso.datetime(),
});
export const fraudCheckSchema = z.object({
    phone: z.string(),
    level: riskLevelSchema,
    reasons: z.array(riskReasonSchema),
    /** FR-03: the tenant asks for an advance at this level. */
    advanceRequired: z.boolean(),
    own: ownHistorySchema,
    /** Empty when no connected courier offers customer history (the section is hidden). */
    courier: z.array(courierHistorySchema),
    /** True while a courier's history is being fetched (look again shortly). */
    courierPending: z.boolean(),
    blocked: z.object({ id: z.uuid(), reason: z.string(), createdAt: z.iso.datetime() }).nullable(),
});
export const fraudCheckQuerySchema = z.object({ phone: z.string().trim().min(6).max(20) });
/** The badge on an order in lists (FR-01/FR-03): level and whether the number is blocked. */
export const orderRiskSchema = z.object({ level: riskLevelSchema, blocked: z.boolean() });
/** FR-03 rules; defaults until first saved (`version` 0). */
export const fraudSettingsSchema = z.object({
    /** Delivered + returned orders needed before a success rate counts (fewer → "new customer"). */
    minOrders: z.int(),
    /** Below this success rate (bp) → high. */
    highBelowBp: z.int(),
    /** Below this success rate (bp) → medium. */
    mediumBelowBp: z.int(),
    /** This many cancelled orders or more → at least medium. */
    cancelledMedium: z.int(),
    /** A new customer's level. */
    newCustomerLevel: riskLevelSchema,
    /** Ask for an advance at this level and above (null: never). */
    advanceAt: riskLevelSchema.nullable(),
    version: z.int(),
});
export const updateFraudSettingsRequestSchema = z
    .object({
    minOrders: z.int().min(1).max(50).optional(),
    highBelowBp: z.int().min(0).max(10_000).optional(),
    mediumBelowBp: z.int().min(0).max(10_000).optional(),
    cancelledMedium: z.int().min(1).max(100).optional(),
    newCustomerLevel: riskLevelSchema.optional(),
    advanceAt: riskLevelSchema.nullable().optional(),
    version: z.int().min(0),
})
    .strict();
/** FR-04 per-tenant blocklist. */
export const blockedPhoneSchema = z.object({
    id: z.uuid(),
    phone: z.string(),
    reason: z.string(),
    createdBy: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
});
export const blockedPhoneListSchema = z.object({
    data: z.array(blockedPhoneSchema),
    nextCursor: z.uuid().nullable(),
});
export const blockedPhoneListQuerySchema = z.object({
    phone: z.string().trim().max(20).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
});
export const blockPhoneRequestSchema = z
    .object({ phone: z.string().trim().min(6).max(20), reason: z.string().trim().min(1).max(250) })
    .strict();
// Endpoints ----------------------------------------------------------------------------------------------------------
const tag = { tag: "shipping" };
const view = { permission: "orders.view" };
export const checkPhone = defineEndpoint({
    ...tag,
    name: "checkPhone",
    method: "GET",
    path: "/v1/fraud-check",
    access: view,
    summary: "Risk of a phone number: own history, courier history (where offered), blocklist, level and why",
    query: fraudCheckQuerySchema,
    status: 200,
    response: fraudCheckSchema,
});
export const getFraudSettings = defineEndpoint({
    ...tag,
    name: "getFraudSettings",
    method: "GET",
    path: "/v1/fraud-settings",
    access: view,
    summary: "The tenant's risk rules",
    status: 200,
    response: fraudSettingsSchema,
});
export const updateFraudSettings = defineEndpoint({
    ...tag,
    name: "updateFraudSettings",
    method: "PATCH",
    path: "/v1/fraud-settings",
    access: { permission: "settings.manage" },
    summary: "Change the risk rules",
    body: updateFraudSettingsRequestSchema,
    status: 200,
    response: fraudSettingsSchema,
    errors: ["CONFLICT"],
});
export const listBlockedPhones = defineEndpoint({
    ...tag,
    name: "listBlockedPhones",
    method: "GET",
    path: "/v1/blocked-phones",
    access: view,
    summary: "Blocked phone numbers, newest first",
    query: blockedPhoneListQuerySchema,
    status: 200,
    response: blockedPhoneListSchema,
});
export const blockPhone = defineEndpoint({
    ...tag,
    name: "blockPhone",
    method: "POST",
    path: "/v1/blocked-phones",
    access: { permission: "blocklist.manage" },
    summary: "Block a phone number (orders for it get a warning)",
    body: blockPhoneRequestSchema,
    status: 201,
    response: blockedPhoneSchema,
    errors: ["PHONE_BLOCKED"],
});
export const unblockPhone = defineEndpoint({
    ...tag,
    name: "unblockPhone",
    method: "DELETE",
    path: "/v1/blocked-phones/{id}",
    access: { permission: "blocklist.manage" },
    summary: "Remove a number from the blocklist",
    params: idParamsSchema,
    status: 204,
});
//# sourceMappingURL=fraud.js.map